import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { allPicked, toggled, withAll } from "./bulk";
import { shortTime } from "./calls";
import { go, useTitle } from "./hooks";
import {
  cancelIds,
  cancelledText,
  cancelText,
  FILTERS,
  KIND_ORDER,
  KIND_TEXT,
  kindOf,
  ofKind,
  readKind,
  readFilter,
  REQUEST_COLUMNS,
  requestCount,
  shortDay,
  requestedBy,
  statusClass,
  statusDetail,
  windowOf,
} from "./requests";
import { RequestPhoto } from "./RequestPhoto";
import { SERVER_PROBLEM, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

type Load = { requests: api.PickupRequest[]; limit: number; can_cancel: boolean } | "server" | "network" | "session" | undefined;

/** Rule P-6: the pickup and service requests, a status filter, and a cancel of all the selected rows. */
export function RequestsPage({
  base,
  org,
  auth,
  user,
  canView,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const [filter, setFilterState] = useState<api.RequestFilter>(() => readFilter(location.search));
  const [kind, setKindState] = useState<api.RequestKind | "all">(() => readKind(location.search));
  const [found, setFound] = useState<Load>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [asking, setAsking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string; session?: boolean } | null>(null);
  const noteLine = useRef<HTMLParagraphElement>(null);
  const [sort, setSort] = useSort("sort", REQUEST_COLUMNS);
  useTitle(`Requests · ${org.name}`);
  const setFilter = (next: api.RequestFilter) => {
    setFilterState(next);
    setPicked(new Set());
    setAsking(false);
    setNote(null);
    const url = new URL(location.href);
    if (next === "open") url.searchParams.delete("status");
    else url.searchParams.set("status", next);
    history.replaceState(history.state, "", url);
  };
  const setKind = (next: api.RequestKind | "all") => {
    setKindState(next);
    setPicked(new Set());
    setAsking(false);
    const url = new URL(location.href);
    if (next === "all") url.searchParams.delete("kind");
    else url.searchParams.set("kind", next);
    history.replaceState(history.state, "", url);
  };
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    setFound(undefined);
    user
      .getIdToken()
      .then((token) => api.pickupRequests(token, filter))
      .then((list) => live && setFound(list))
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        if (live) setFound(code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
    return () => {
      live = false;
    };
  }, [user, filter, attempt, canView]);
  if (canView === false) {
    return (
      <main data-testid="requests-hidden">
        <h1 tabIndex={-1}>Requests</h1>
        <p>Your role does not show requests.</p>
      </main>
    );
  }
  const rows = typeof found === "object" ? sortRows(ofKind(found.requests, kind), REQUEST_COLUMNS, sort) : [];
  const selectable = cancelIds(rows);
  const pickedIds = selectable.filter((id) => picked.has(id));
  const cancel = async () => {
    setSaving(true);
    setNote(null);
    try {
      const done = await api.cancelRequests(await user.getIdToken(), pickedIds);
      setNote({ ok: true, text: cancelledText(done.cancelled, done.unchanged) });
      setPicked(new Set());
      setAsking(false);
      setAttempt((n) => n + 1);
    } catch (e) {
      const failure = saveFailure(e, "The cancel");
      setNote({ ok: false, text: failure.text, session: !failure.ok && failure.session });
    } finally {
      setSaving(false);
      requestAnimationFrame(() => noteLine.current?.focus());
    }
  };
  return (
    <main className="issues-page wide" data-testid="requests-page">
      <h1 tabIndex={-1}>Requests</h1>
      <div className="tabs" role="group" aria-label="Which requests">
        {FILTERS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={filter === option.value}
            data-testid={`requests-${option.value}`}
            onClick={() => setFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      <label>
        Request{" "}
        <select value={kind} onChange={(e) => setKind(e.target.value as api.RequestKind | "all")} data-testid="requests-kind">
          <option value="all">All kinds</option>
          {KIND_ORDER.map((value) => (
            <option key={value} value={value}>
              {KIND_TEXT[value]}
            </option>
          ))}
        </select>
      </label>
      <p ref={noteLine} tabIndex={-1} role="status" data-testid="requests-note" className={note && !note.ok ? "error" : undefined}>
        {note?.text}
        {note?.session && (
          <>
            {" "}
            <button type="button" onClick={() => signOut(auth)}>
              Sign in again
            </button>
          </>
        )}
      </p>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="requests-none">No requests with this status and kind.</p>
      ) : (
        <>
          {rows.length >= found.limit && (
            <p className="sub" data-testid="requests-limit">
              The list shows the first {found.limit} requests, the soonest need-by day first.
            </p>
          )}
          {found.can_cancel && selectable.length > 0 && (
            <p className="bulk-bar" data-testid="requests-bulk">
              <label className="check">
                <input
                  type="checkbox"
                  data-testid="requests-pick-all"
                  checked={allPicked(picked, selectable)}
                  onChange={(e) => setPicked((before) => withAll(before, selectable, e.target.checked))}
                />{" "}
                Select all open ({selectable.length})
              </label>{" "}
              <span data-testid="requests-count">{pickedIds.length} selected</span>{" "}
              <button type="button" data-testid="requests-cancel" disabled={pickedIds.length === 0 || saving} onClick={() => setAsking(true)}>
                Cancel the requests
              </button>
              {pickedIds.length > 0 && (
                <>
                  {" "}
                  <button type="button" className="quiet" data-testid="requests-clear" onClick={() => setPicked(new Set())}>
                    Clear the selection
                  </button>
                </>
              )}
            </p>
          )}
          {asking && pickedIds.length > 0 && (
            <p className="confirm" role="group" aria-label="Cancel the requests" data-testid="requests-confirm">
              {cancelText(pickedIds.length)} The planner then does not plan them.{" "}
              <button type="button" className="danger" data-testid="requests-confirm-yes" disabled={saving} onClick={cancel}>
                {saving ? "Cancelling…" : `Cancel ${requestCount(pickedIds.length)}`}
              </button>{" "}
              <button type="button" data-testid="requests-confirm-no" disabled={saving} onClick={() => setAsking(false)}>
                {pickedIds.length === 1 ? "Keep it" : "Keep them"}
              </button>
            </p>
          )}
          <SortSelect id="requests-sort" columns={REQUEST_COLUMNS} sort={sort} onSort={setSort} fallback="Soonest need-by day first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Requests">
            <table className="list requests-table" data-testid="requests-table" data-cols="requests-sort">
              <thead>
                <tr>
                  {REQUEST_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                  <th scope="col">Photo</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((request) => (
                  <tr key={request.id} data-testid="request-row" data-status={request.status} className={picked.has(request.id) ? "picked" : undefined}>
                    <th scope="row" className="account-number">
                      {found.can_cancel && request.can_cancel && (
                        <label className="pick-target">
                          <input
                            type="checkbox"
                            className="pick"
                            data-testid="request-pick"
                            aria-label={`Select the request of ${request.account_number}`}
                            checked={picked.has(request.id)}
                            onChange={() => setPicked((before) => toggled(before, request.id))}
                          />
                        </label>
                      )}
                      <a href={`${base}/accounts/${request.account_id}`} onClick={go}>
                        {request.account_number}
                      </a>{" "}
                      <span className="sub clip name" title={request.account_name}>
                        {request.account_name}
                      </span>
                    </th>
                    <td data-label="Status" className="status">
                      <span className={statusClass(request.status)}>{statusDetail(request)}</span>
                    </td>
                    <td data-label="Request" data-testid="request-kind">
                      {KIND_TEXT[kindOf(request)]}
                    </td>
                    <td data-label="Need by">{shortDay(request.need_by)}</td>
                    <td data-label="Time">{windowOf(request)}</td>
                    <td data-label="Requested" className="requested" data-testid="request-requested">
                      {shortTime(request.requested_at, org.time_zone)}
                      <span className="sub requested-by" title={request.requested_by ?? undefined}>
                        {requestedBy(request)}
                      </span>
                    </td>
                    <td data-label="Note" className="wrap">
                      {request.note || "–"}
                    </td>
                    <td data-label="Photo">
                      {request.photo_id ? (
                        <RequestPhoto load={async () => (await api.requestPhoto(await user.getIdToken(), request.id)).url} testId="request-photo" />
                      ) : (
                        "–"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}
